<template>
  <div :class="visibilityClass">
    <li
      class="message-item"
      :class="{
        outbound: isMine,
        inbound: !isMine,
        'info-message': message.infoMessage
      }"
    >
      <div class="msg-container">
        <template v-if="!message.infoMessage">
          <div v-if="!isMine && (type !== 'PRIVATE' || !privateChatTarget)" class="msg-actions">
            <person
              :person="sender"
              :options="{
                actions: true,
                inlineActions: false,
                avatarOnly: true
              }"
            ></person>
          </div>
          <div class="msg-content">
            <div v-if="message.isHTML" v-html="message.messageBody" class="message"></div>
            <div v-else class="message">
              <span class="body">{{ message.messageBody }}</span>
            </div>
            <div v-if="type !== 'PRIVATE' || !privateChatTarget" class="msg-fromto">
              <template v-if="!isMine">
                <span class="sender">
                  {{ getUserFullName(getUserByUUID(message.userUUID)) }}
                </span>
              </template>
              <template v-if="isMine && type === 'PRIVATE' && message.recipientUUID">
                <span class="addressee">
                  <i class="fa fa-fw fa-level-up fa-rotate-90" aria-hidden="true"></i>
                  {{ getUserFullName(recipient) }}
                </span>
              </template>
            </div>
          </div>
          <div class="msg-visibility" v-if="showMessageVisibility" @click="changeVisibility(message.messageUUID)">
            <template v-if="!loading">
              <i v-if="message.messageStatus === messageStatuses.PUBLISHED" class="fa fa-eye-slash clickable"></i>
              <i v-else class="fa fa-eye clickable"></i>
            </template>
            <vue-element-loading v-else active size="30" :color="primaryColor"></vue-element-loading>
          </div>
        </template>

        <template v-if="message.infoMessage">
          <div v-if="message.isHTML" v-html="message.messageBody" class="info-message"></div>
          <div v-else class="message info-message">
            <span class="body">{{ message.messageBody }}</span>
          </div>
        </template>
        <div class="mtime">
          {{ formatMessageDate(message) }}
        </div>
      </div>
    </li>
  </div>
</template>

<script>
import Person from "@/components/DigitalEvent/RoomList/Room/People/Person";
import VueElementLoading from "vue-element-loading";
import DayJsMixin from "@/components/DigitalEvent/RoomList/Room/mixins/DayJsMixin.js";
import { CHAT_MODES, MESSAGE_STATUSES, WSS_MESSAGE_TYPES } from "@/helpers/constants";
import { mapGetters } from "vuex";

export default {
  name: "ChatMessage",
  components: {
    person: Person,
    VueElementLoading
  },
  mixins: [DayJsMixin],
  props: {
    message: {
      type: Object,
      required: true
    },
    type: {
      type: String,
      required: true,
      validator(value) {
        return [CHAT_MODES.ROOM, CHAT_MODES.TABLE, CHAT_MODES.PRIVATE].indexOf(value) !== -1;
      }
    }
  },
  data() {
    return {
      loading: false
    };
  },
  computed: {
    ...mapGetters({
      publicTableUsers: "tables/publicTableUsers",
      currentUserPermissions: "users/currentUserPermissions",
      group: "room/activeRoom"
    }),
    /**
     * Do message belongs to current user
     * @return {boolean}
     */
    isMine() {
      return this.message.userUUID && this.message.userUUID === this.$store.state.users.user.user_uuid;
    },
    /**
     * Checking that the current user is Admin
     */
    isAdmin() {
      return this.$store.state.users.user && this.$store.state.users.user.is_admin;
    },
    /**
     * Check id there is currently a user targeted for private chat
     * update the first name and last name from profile if we have a target and they have a profile saved
     * @return {Object|null}
     */
    privateChatTarget() {
      let chatTarget = this.$store.state.chat.privateChatTarget;
      if (chatTarget) {
        let targetUser = this.$store.getters["users/getUser"](chatTarget.userUUID);
        if (targetUser.profile && targetUser.profile.firstName) {
          chatTarget.firstName = targetUser.profile.firstName;
          chatTarget.lastName = targetUser.profile.lastName;
        } else if (targetUser.firstName) {
          chatTarget.firstName = targetUser.firstName;
          chatTarget.lastName = targetUser.lastName;
        }
      }
      return chatTarget;
    },
    /**
     * Get message sender user model
     * @return {Object}
     */
    sender() {
      return this.$store.getters["users/getUser"](this.message.userUUID);
    },
    /**
     * Get message recipient user model
     * @return {*}
     */
    recipient() {
      return this.$store.getters["users/getUser"](this.message.recipientUUID);
    },
    /**
     * Get the loggedin user's table
     * @return {Object}
     */
    currentUserPermissions() {
      return this.$store.getters["users/currentUserPermissions"];
    },
    showMessageVisibility() {
      return this.isAdmin && !this.isMine && this.type === CHAT_MODES.ROOM;
    },
    visibilityClass() {
      // TODO: remove messages from list, DO NOT use class to hide them!
      if (this.message.messageStatus === MESSAGE_STATUSES.HIDDEN) {
        return this.isAdmin ? "yellow-message" : "hide-message";
      }
      return "";
    },
    isUserFromPublic() {
      return this.publicTableUsers.some(user => user.userUUID === this.message.userUUID);
    },
    messageStatuses() {
      return MESSAGE_STATUSES;
    },
    primaryColor() {
      return this.$store.state.themeStyles.primaryColor.color;
    }
  },
  methods: {
    /**
     * Filter user by message and change tab to private
     */
    filterUserByMessage() {
      this.$store.commit("chat/setPrivateChatTarget", {
        userUUID: this.message.userUUID,
        firstName: this.message.meta.userMeta.firstName,
        lastName: this.message.meta.userMeta.lastName,
        domain: this.message.meta.userMeta.meta.domain
      });
      this.$store.commit("chat/changeChatMode", {
        mode: CHAT_MODES.PRIVATE,
        scrollPage: false
      });
      this.$root.$emit("activateTab", "#tab-chat-private");
    },

    /**
     * Get user info by User.uuid (from room state)
     * @param userUUID {String}
     * @return {Object}
     */
    getUserByUUID(userUUID) {
      return this.$store.getters["users/getUser"](userUUID);
    },

    /**
     * Template proxy to helper method
     * @param user {Object}
     * @return {string}
     */
    getUserFullName(user) {
      return this.$store.getters["users/getUserFullName"](user);
    },
    /**
     * Change visibility method
     * @param messageUUID {string}
     */
    async changeVisibility(messageUUID) {
      this.loading = true;
      let status =
        this.message.messageStatus === MESSAGE_STATUSES.PUBLISHED
          ? MESSAGE_STATUSES.HIDDEN
          : MESSAGE_STATUSES.PUBLISHED;
      await this.$store.dispatch("chat/messageUpdate", {
        messageUUID: messageUUID,
        groupUUID: this.group.uuid,
        message: {
          messageStatus: status,
          messageType: WSS_MESSAGE_TYPES.ROOM_MESSAGE
        }
      });
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.velmld-overlay {
  background-color: transparent !important;
}
</style>
